/**
 * Task Request List — T1→T4 Push Notification Chain
 * Visual language: matches My Tasks (cream bg, Playfair serif heading, gold accents, clean card rows)
 *
 * Acknowledging a notification removes it from this list and sets the tier to In Progress.
 * Notifications auto-expire after 45 days (server-side).
 */

import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { CheckCircle2, ChevronDown, ChevronUp, Search } from "lucide-react";

// ── Tier colours ───────────────────────────────────────────────────────────────
const TIER_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  T1: { bg: "oklch(0.94 0.04 180 / 30%)", text: "oklch(0.35 0.12 160)", border: "oklch(0.75 0.10 160 / 40%)" },
  T2: { bg: "oklch(0.94 0.04 220 / 30%)", text: "oklch(0.35 0.12 220)", border: "oklch(0.65 0.12 220 / 40%)" },
  T3: { bg: "oklch(0.94 0.04 260 / 30%)", text: "oklch(0.35 0.12 260)", border: "oklch(0.65 0.12 260 / 40%)" },
  T4: { bg: "oklch(0.94 0.04 300 / 30%)", text: "oklch(0.35 0.12 300)", border: "oklch(0.65 0.12 300 / 40%)" },
};

const TIER_LABELS: Record<string, string> = {
  T1: "B/Feed",
  T2: "Code",
  T3: "ATB",
  "T3-C": "C-P&L",
};

/** Map internal tier key to display label */
function tierDisplay(tier: string): string {
  return tier; // T3-C is now the canonical name (T4 no longer exists)
}

/** Shorten company name to ≤28 chars */
function shortName(name: string): string {
  if (name.length <= 28) return name;
  return name.substring(0, 26).trim() + "…";
}

/** Relative time helper */
function relativeTime(dateStr: string | Date): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function TaskRequestList() {
  const { user } = useAuth();
  const [acknowledging, setAcknowledging] = useState<Set<number>>(new Set());
  const [markingAllFor, setMarkingAllFor] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  const userEmail = user?.email ?? "";

  const { data: notifications = [], isLoading } = trpc.taskNotifications.list.useQuery(
    { recipientEmail: userEmail },
    { enabled: !!userEmail, refetchInterval: 30_000 }
  );

  const utils = trpc.useUtils();

  const acknowledgeMutation = trpc.taskNotifications.acknowledge.useMutation({
    onMutate: async ({ id }) => {
      setAcknowledging((prev) => new Set(prev).add(id));
    },
    onSuccess: () => {
      utils.taskNotifications.list.invalidate({ recipientEmail: userEmail });
      utils.taskNotifications.count.invalidate({ recipientEmail: userEmail });
      utils.taskNotifications.countByEmails.invalidate();
    },
    onError: (err, { id }) => {
      setAcknowledging((prev) => { const s = new Set(prev); s.delete(id); return s; });
      toast.error("Failed to acknowledge: " + err.message);
    },
  });

  const acknowledgeAllMutation = trpc.taskNotifications.acknowledgeAll.useMutation({
    onSuccess: (data) => {
      utils.taskNotifications.list.invalidate({ recipientEmail: userEmail });
      utils.taskNotifications.count.invalidate({ recipientEmail: userEmail });
      utils.taskNotifications.countByEmails.invalidate();
      toast.success(`Started ${data.count} task request${data.count !== 1 ? "s" : ""}`);
    },
    onError: (err) => toast.error("Failed: " + err.message),
  });

  const handleAcknowledge = (notif: { id: number; clientName: string; toTier: string; recipientEmail: string }) => {
    acknowledgeMutation.mutate({
      id: notif.id,
      clientName: notif.clientName,
      toTier: notif.toTier,
      recipientEmail: notif.recipientEmail,
    });
  };

  const handleAcknowledgeAll = () => {
    if (notifications.length === 0) return;
    acknowledgeAllMutation.mutate({ recipientEmail: userEmail });
  };

  const handleMarkAllForCompany = async (clientName: string, ids: number[]) => {
    setMarkingAllFor((prev) => new Set(prev).add(clientName));
    setAcknowledging((prev) => { const s = new Set(prev); ids.forEach(id => s.add(id)); return s; });
    try {
      await Promise.all(ids.map(id => {
        const n = notifications.find(x => x.id === id);
        return acknowledgeMutation.mutateAsync({
          id,
          clientName: n?.clientName,
          toTier: n?.toTier,
          recipientEmail: n?.recipientEmail,
        });
      }));
      toast.success(`All tasks for ${shortName(clientName)} started`);
    } catch {
      // individual errors handled by mutation
    } finally {
      setMarkingAllFor((prev) => { const s = new Set(prev); s.delete(clientName); return s; });
    }
  };

  const pendingNotifications = notifications.filter((n) => !acknowledging.has(n.id));
  const acknowledgedNotifications = notifications.filter((n) => acknowledging.has(n.id));

  // Filter by search
  const filteredNotifications = useMemo(() => {
    if (!search.trim()) return pendingNotifications;
    const q = search.toLowerCase();
    return pendingNotifications.filter(n =>
      n.clientName?.toLowerCase().includes(q) ||
      n.toTier?.toLowerCase().includes(q) ||
      n.fromTier?.toLowerCase().includes(q) ||
      n.completedByName?.toLowerCase().includes(q)
    );
  }, [pendingNotifications, search]);

  // Group by company name
  const grouped = useMemo(() => {
    const map = new Map<string, typeof filteredNotifications>();
    for (const n of filteredNotifications) {
      if (!map.has(n.clientName)) map.set(n.clientName, []);
      map.get(n.clientName)!.push(n);
    }
    return Array.from(map.entries());
  }, [filteredNotifications]);

  // Stat counts
  const tierCounts = useMemo(() => {
    const counts: Record<string, number> = { T1: 0, T2: 0, T3: 0, T4: 0 };
    for (const n of pendingNotifications) {
      if (counts[n.toTier] !== undefined) counts[n.toTier]++;
    }
    return counts;
  }, [pendingNotifications]);

  const STAT_TILES = [
    { key: "T1", label: "T1",
      bg: "bg-[oklch(0.95_0.03_180)]", border: "border-[oklch(0.85_0.06_160)]",
      numColor: "text-[oklch(0.30_0.12_160)]", labelColor: "text-[oklch(0.45_0.10_160)]" },
    { key: "T2", label: "T2",
      bg: "bg-[oklch(0.95_0.03_220)]", border: "border-[oklch(0.82_0.06_220)]",
      numColor: "text-[oklch(0.30_0.12_220)]", labelColor: "text-[oklch(0.45_0.10_220)]" },
    { key: "T3", label: "T3",
      bg: "bg-[oklch(0.95_0.03_260)]", border: "border-[oklch(0.82_0.06_260)]",
      numColor: "text-[oklch(0.30_0.12_260)]", labelColor: "text-[oklch(0.45_0.10_260)]" },
    { key: "T3-C", label: "C-P&L",
      bg: "bg-[oklch(0.95_0.03_300)]", border: "border-[oklch(0.82_0.06_300)]",
      numColor: "text-[oklch(0.30_0.12_300)]", labelColor: "text-[oklch(0.45_0.10_300)]" },
  ];

  return (
    <DashboardLayout noPadding>
      <div className="min-h-screen bg-[#f5f0e8]">
        <div className="max-w-5xl mx-auto px-6 py-8">

          {/* ── White card wrapper (matches My Tasks) ── */}
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden mb-6">

            {/* Section header */}
            <div className="px-6 pt-6 pb-4 border-b border-stone-100">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-stone-400 mb-1">
                    INCOMING WORK
                  </p>
                  <h2
                    style={{ fontFamily: "'Playfair Display', serif" }}
                    className="text-2xl font-black tracking-tight text-stone-900"
                  >
                    TASK REQUESTS
                  </h2>
                  <p className="text-xs text-stone-400 mt-0.5">
                    {pendingNotifications.length > 0
                      ? `${pendingNotifications.length} file${pendingNotifications.length !== 1 ? "s" : ""} ready to start`
                      : "All clear — no pending requests"}
                  </p>
                </div>
                {pendingNotifications.length > 0 && (
                  <button
                    onClick={handleAcknowledgeAll}
                    disabled={acknowledgeAllMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm tracking-wide transition-all active:scale-95 disabled:opacity-60 shrink-0 mt-1 bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                  >
                    {acknowledgeAllMutation.isPending ? "Starting…" : `Start All (${pendingNotifications.length})`}
                  </button>
                )}
              </div>

              {/* Stat boxes — same style as My Tasks */}
              <div className="grid grid-cols-4 gap-3 mt-5">
                {STAT_TILES.map(s => (
                  <div
                    key={s.key}
                    className={`rounded-xl border px-4 py-3 text-left ${s.bg} ${s.border}`}
                  >
                    <p className={`text-2xl font-black ${s.numColor}`}>{tierCounts[s.key] ?? 0}</p>
                    <p className={`text-xs font-bold tracking-widest uppercase mt-0.5 ${s.labelColor}`}>{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Search — same style as My Tasks */}
              <div className="relative mt-4">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <input
                  type="text"
                  placeholder="Search by client, tier, or bookkeeper…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="w-full h-10 pl-10 pr-4 rounded-xl border border-stone-200 bg-stone-50 text-sm text-stone-700 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition-colors"
                />
              </div>
            </div>

            {/* Content area */}
            <div className="p-5 space-y-3">

          {/* ── Loading ── */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-16 rounded-xl bg-stone-100 animate-pulse" />
              ))}
            </div>
          )}

          {/* ── Empty state ── */}
          {!isLoading && pendingNotifications.length === 0 && (
            <div className="text-center py-12 text-stone-400">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-bold text-sm">All clear</p>
              <p className="text-xs mt-1">You'll be notified here when a prior tier is completed.</p>
            </div>
          )}

          {/* ── Grouped notification rows ── */}
          {!isLoading && grouped.length > 0 && (
            <div className="space-y-3">
              {grouped.map(([clientName, items]) => {
                const allIds = items.map(n => n.id);
                const isMarkingAll = markingAllFor.has(clientName);

                return (
                  <div
                    key={clientName}
                    className="bg-white rounded-xl border border-[oklch(0.88_0.04_75)] shadow-sm overflow-hidden"
                  >
                    {/* Company header */}
                    <div className="flex items-center justify-between px-4 py-3 bg-[oklch(0.96_0.03_75)] border-b border-[oklch(0.88_0.04_75)]">
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{
                            background: "oklch(0.65 0.18 142)",
                            boxShadow: "0 0 0 3px oklch(0.65 0.18 142 / 20%)",
                          }}
                        />
                        <span className="text-sm font-bold text-[oklch(0.18_0.04_240)] tracking-tight">
                          {shortName(clientName)}
                        </span>
                        <span className="text-xs text-[oklch(0.58_0.03_240)]">
                          {items.length} tier{items.length !== 1 ? "s" : ""} ready
                        </span>
                      </div>
                      <button
                        onClick={() => handleMarkAllForCompany(clientName, allIds)}
                        disabled={isMarkingAll}
                        className="text-xs font-bold px-3 py-1 rounded-lg transition-all disabled:opacity-50"
                        style={{
                          background: "oklch(0.65 0.18 142 / 12%)",
                          color: "oklch(0.42 0.16 142)",
                          border: "1px solid oklch(0.65 0.18 142 / 35%)",
                        }}
                      >
                        {isMarkingAll ? "Starting…" : "Start All"}
                      </button>
                    </div>

                    {/* Tier rows */}
                    {items.map((notif, idx) => {
                      const toColor = TIER_COLORS[notif.toTier] ?? TIER_COLORS["T1"];
                      const fromColor = TIER_COLORS[notif.fromTier] ?? TIER_COLORS["T1"];
                      const isDone = acknowledging.has(notif.id);
                      const isLast = idx === items.length - 1;

                      return (
                        <div
                          key={notif.id}
                          className={`flex items-center gap-3 px-4 py-3 transition-opacity ${isDone ? "opacity-40" : ""} ${!isLast ? "border-b border-[oklch(0.91_0.03_75)]" : ""}`}
                        >
                          {/* From tier badge */}
                          <span
                            className="text-[11px] font-bold px-2.5 py-0.5 rounded-full flex-shrink-0"
                            style={{
                              background: fromColor.bg,
                              color: fromColor.text,
                              border: `1px solid ${fromColor.border}`,
                            }}
                          >
                            {tierDisplay(notif.fromTier)} {TIER_LABELS[notif.fromTier] ?? notif.fromTier}
                          </span>

                          {/* Arrow */}
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="oklch(0.70 0.03 240)" strokeWidth="2" className="flex-shrink-0">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
                          </svg>

                          {/* To tier badge — highlighted (ready) */}
                          <span
                            className="text-[11px] font-bold px-2.5 py-0.5 rounded-full flex-shrink-0"
                            style={{
                              background: "oklch(0.65 0.18 142 / 15%)",
                              color: "oklch(0.42 0.16 142)",
                              border: "1px solid oklch(0.65 0.18 142 / 35%)",
                            }}
                          >
                            {tierDisplay(notif.toTier)} {TIER_LABELS[notif.toTier] ?? notif.toTier}
                          </span>

                          {/* Spacer */}
                          <div className="flex-1 min-w-0" />

                          {/* Completed by + time */}
                          <span className="text-xs text-[oklch(0.58_0.03_240)] whitespace-nowrap flex-shrink-0">
                            {notif.completedByName} · {relativeTime(notif.createdAt)}
                          </span>

                          {/* Start button */}
                          <button
                            onClick={() => handleAcknowledge(notif)}
                            disabled={isDone || acknowledgeMutation.isPending}
                            className="text-xs font-bold px-3.5 py-1.5 rounded-lg flex-shrink-0 transition-all disabled:cursor-default"
                            style={isDone ? {
                              background: "oklch(0.93 0.02 240)",
                              color: "oklch(0.60 0.03 240)",
                              border: "1px solid oklch(0.87 0.02 240)",
                            } : {
                              background: "oklch(0.72 0.14 75)",
                              color: "white",
                              border: "1px solid transparent",
                              boxShadow: "0 2px 6px oklch(0.72 0.14 75 / 30%)",
                            }}
                          >
                            {isDone ? "✓ Started" : "Start"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

            </div>{/* end content area */}
          </div>{/* end white card */}

          {/* ── Recently started (acknowledged) ── */}
          {acknowledgedNotifications.length > 0 && (
            <div className="mt-8">
              <button
                onClick={() => setShowAcknowledged(!showAcknowledged)}
                className="flex items-center gap-2 text-sm font-bold text-[oklch(0.55_0.03_240)] hover:text-[oklch(0.35_0.04_240)] mb-3 tracking-wide uppercase"
              >
                {showAcknowledged ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Recently Started ({acknowledgedNotifications.length})
              </button>
              {showAcknowledged && (
                <div className="bg-white rounded-xl border border-[oklch(0.88_0.04_75)] shadow-sm overflow-hidden opacity-60">
                  {acknowledgedNotifications.map((notif, idx) => {
                    const toColor = TIER_COLORS[notif.toTier] ?? TIER_COLORS["T1"];
                    const isLast = idx === acknowledgedNotifications.length - 1;
                    return (
                      <div
                        key={notif.id}
                        className={`flex items-center gap-3 px-4 py-3 ${!isLast ? "border-b border-[oklch(0.91_0.03_75)]" : ""}`}
                      >
                        <span className="text-xs font-bold text-[oklch(0.50_0.14_145)]">✓</span>
                        <span className="text-sm font-semibold text-[oklch(0.35_0.04_240)] line-through">
                          {shortName(notif.clientName)}
                        </span>
                        <span
                          className="text-[11px] font-bold px-2.5 py-0.5 rounded-full"
                          style={{ background: toColor.bg, color: toColor.text, border: `1px solid ${toColor.border}` }}
                        >
                          {tierDisplay(notif.toTier)}
                        </span>
                        <div className="flex-1" />
                        <span className="text-xs text-[oklch(0.65_0.03_240)]">{relativeTime(notif.createdAt)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </DashboardLayout>
  );
}
